'use client';

import { Box, Card, Group, Paper, Stack, Text } from '@mantine/core';
import type { CSSProperties } from 'react';
import type { Theme } from '@/types/interview';

interface InterviewProgressProps {
  themes: Theme[];
  /** Topic ids the interviewer marked COMPLETE (LUM-134 mark_topic).
   *  Unknown ids are ignored; with no known id the outline is static. */
  completedTopicIds?: readonly string[];
}

type TopicState = 'completed' | 'current' | 'upcoming';

const NO_MARKS: readonly string[] = [];

const STATIC_STYLE = { backgroundColor: 'var(--mantine-color-gray-0)' };

const LIVE_STYLE: Record<TopicState, CSSProperties> = {
  completed: { ...STATIC_STYLE, opacity: 0.6 },
  current: { backgroundColor: 'var(--mantine-color-blue-0)' },
  upcoming: STATIC_STYLE,
};

export function InterviewProgress({
  themes,
  completedTopicIds = NO_MARKS,
}: Readonly<InterviewProgressProps>) {
  const ordered = [...themes].sort((a, b) => a.order - b.order);
  const isCompleted = (theme: Theme) =>
    completedTopicIds.some(id => id === theme.id);
  // Live states only once a mark names a real topic — until then (tool
  // off, or no event yet) the outline renders exactly as the static one.
  const live = ordered.some(isCompleted);
  // Current = first not-completed topic in order. Completion is never
  // undone here: states derive only from the (monotonic) marked set.
  const currentId = live ? ordered.find(t => !isCompleted(t))?.id : undefined;
  const stateOf = (theme: Theme): TopicState | undefined => {
    if (!live) return undefined;
    if (isCompleted(theme)) return 'completed';
    return theme.id === currentId ? 'current' : 'upcoming';
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <Text size="lg" fw={600} mb="xs">
          Interview Outline
        </Text>

        <Stack gap="sm">
          {ordered.map(theme => {
            const state = stateOf(theme);
            const completed = state === 'completed';
            return (
              <Paper
                key={theme.id}
                data-topic-id={theme.id}
                data-state={state}
                aria-current={state === 'current' ? 'step' : undefined}
                p="sm"
                radius="md"
                style={state ? LIVE_STYLE[state] : STATIC_STYLE}
              >
                <Group gap="sm" wrap="nowrap">
                  <Text
                    size="lg"
                    c={state === 'current' ? 'blue' : 'dimmed'}
                    style={{ width: 24, textAlign: 'center' }}
                  >
                    {completed ? '✓' : '•'}
                  </Text>
                  <Box style={{ flex: 1 }}>
                    <Text
                      size="sm"
                      fw={500}
                      c={completed ? 'dimmed' : undefined}
                    >
                      {theme.name}
                    </Text>
                    {theme.description && (
                      <Text size="xs" c="dimmed" mt={2}>
                        {theme.description}
                      </Text>
                    )}
                  </Box>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
}
